import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  AbschnittAdmin,
  KategorieAdmin,
  KategorieRequest,
  VorlageAdmin
} from '../../../Models/kategorie-admin.model';
import { KategorieAdminService } from '../../../Services/kategorie-admin.service';

/**
 * Verwaltung der Prüfstruktur: Kategorie → Abschnitt → Prüfpunkt.
 *
 * Was hier konfiguriert wird, sind **Vorlagen**. Erst wenn eine Kategorie einer
 * Messstelle zugewiesen wird, entstehen daraus konkrete Prüfpunkte mit Status,
 * Bemerkung und Quittierung – diese Seite berührt Prüfergebnisse nie.
 *
 * Schutz bereits verwendeter Strukturen:
 * - Eine Kategorie an Messstellen lässt sich nicht löschen, nur deaktivieren.
 * - Beim Löschen von Abschnitt oder Prüfpunkt bleiben bereits erfasste Ergebnisse
 *   an den Messstellen bestehen; sie verlieren nur den Vorlagenbezug.
 */
@Component({
  selector: 'app-kategorie-admin',
  templateUrl: './kategorie-admin.component.html',
  styleUrls: ['./kategorie-admin.component.css']
})
export class KategorieAdminComponent implements OnInit {
  kategorien: KategorieAdmin[] = [];
  dupliziertKategorieId: number | null = null;

  isLoading = true;
  loadError = '';

  /** Id der aufgeklappten Kategorie. Null = alle zu. */
  offeneKategorieId: number | null = null;

  // ── Kategorie-Formular ──
  formOpen = false;
  editId: number | null = null;
  saving = false;
  formError = '';
  form: KategorieRequest = this.leeresFormular();

  // ── Inline-Bearbeitung ──

  inlineEdit: {
    typ: 'kategorie' | 'abschnitt' | 'punkt';
    id: number;
  } | null = null;

  inlineName = '';
  inlineBeschreibung = '';
  inlineSaving = false;
  inlineError = '';

  istInlineEdit(
    typ: 'kategorie' | 'abschnitt' | 'punkt',
    id: number
  ): boolean {
    return this.inlineEdit?.typ === typ &&
      this.inlineEdit?.id === id;
  }

  inlineAbbrechen(): void {
    if (this.inlineSaving) return;

    this.inlineEdit = null;
    this.inlineName = '';
    this.inlineBeschreibung = '';
    this.inlineError = '';
  }

  inlineStarten(
    typ: 'kategorie' | 'abschnitt' | 'punkt',
    id: number,
    name: string,
    beschreibung = ''
  ): void {
    if (this.busy || this.inlineSaving) return;

    this.inlineEdit = { typ, id };
    this.inlineName = name;
    this.inlineBeschreibung = beschreibung;
    this.inlineError = '';
  }

  inlineSpeichern(): void {
    if (!this.inlineEdit || this.inlineSaving || this.busy) return; 

    const name = this.inlineName.trim();
    if (!name) {
      this.inlineError = 'Der Name darf nicht leer sein.';
      return;
    }

    const { typ, id } = this.inlineEdit;
    let aufruf;

    if (typ === 'kategorie') {
      const kategorie = this.kategorien.find(k => k.id === id);
      if (!kategorie) return;

      aufruf = this.service.updateKategorie(id, {
        name,
        beschreibung: this.inlineBeschreibung.trim() || null,
        color: kategorie.color ?? null,
        sortOrder: kategorie.sortOrder,
        isActive: kategorie.isActive
      });

    } else if (typ === 'abschnitt') {
      aufruf = this.service.updateAbschnitt(id, { name });

    } else {
      const vorlage = this.kategorien
        .flatMap(k => k.abschnitte)
        .flatMap(a => a.vorlagen)
        .find(v => v.id === id);

      if (!vorlage) return;

      aufruf = this.service.updateVorlage(id, {
        bezeichnung: name,
        abschnittId: vorlage.abschnittId
      });
    }

    this.inlineSaving = true;
    this.inlineError = '';

    aufruf.subscribe({
      next: aktualisiert => {
        this.inlineSaving = false;
        this.uebernehmen(aktualisiert);
        this.inlineAbbrechen();
        this.snackBar.open(
          'Änderung gespeichert.',
          'OK',
          { duration: 2500 }
        );
      },
      error: err => {
        this.inlineSaving = false;
        this.inlineError =
          err?.error?.message ??
          'Die Änderung konnte nicht gespeichert werden.';
      }
    });
  } 
  // ── Eingaben je Kategorie/Abschnitt ──
  /** Neuer Abschnittsname, je Kategorie-Id. */
  neuerAbschnitt: { [kategorieId: number]: string } = {};
  /** Neue Prüfpunkt-Bezeichnung, je Abschnitt-Id. */
  neuerPunkt: { [abschnittId: number]: string } = {};

  /** Läuft gerade ein Schreibvorgang? Sperrt die Bedienelemente. */
  busy = false;

  constructor(
    private service: KategorieAdminService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.lade();
  }

  private lade(): void {
    this.isLoading = true;
    this.loadError = '';

    this.service.getAlle().subscribe({
      next: kategorien => {
        this.kategorien = kategorien ?? [];
        this.isLoading = false;
      },
      error: err => {
        this.loadError = err?.status === 403
          ? 'Für die Verwaltung von Kategorien fehlt die Berechtigung.'
          : 'Die Kategorien konnten nicht geladen werden.';
        this.isLoading = false;
      }
    });
  }

  /** Ersetzt eine Kategorie im Bestand durch die Serverantwort. */
  private uebernehmen(aktualisiert: KategorieAdmin): void {
    const index = this.kategorien.findIndex(k => k.id === aktualisiert.id);
    if (index >= 0) {
      this.kategorien[index] = aktualisiert;
    } else {
      this.kategorien.push(aktualisiert);
    }
    this.busy = false;
  }

  private fehler(err: any, standard: string): void {
    this.busy = false;
    this.snackBar.open(
      err?.error?.message ?? (err?.status === 403 ? 'Dafür fehlt die Berechtigung.' : standard),
      'OK',
      { duration: 4000 }
    );
  }

  // ── Aufklappen ──

  toggle(kategorie: KategorieAdmin): void {
    if (this.inlineEdit !== null) return;
    this.offeneKategorieId = this.offeneKategorieId === kategorie.id ? null : kategorie.id;
  }

  istOffen(kategorie: KategorieAdmin): boolean {
    return this.offeneKategorieId === kategorie.id;
  }

  punkteGesamt(kategorie: KategorieAdmin): number {
    return kategorie.abschnitte.reduce((summe, a) => summe + a.vorlagen.length, 0);
  }

  // ── Kategorie anlegen/bearbeiten ──

  neueKategorie(): void {
    this.editId = null;
    this.form = this.leeresFormular();
    this.form.sortOrder = this.kategorien.length;
    this.formError = '';
    this.formOpen = true;
  }

  bearbeiten(kategorie: KategorieAdmin): void {
  this.inlineStarten(
    'kategorie',
    kategorie.id,
    kategorie.name,
    kategorie.beschreibung ?? ''
  );
}

  abbrechen(): void {
    this.formOpen = false;
    this.editId = null;
    this.formError = '';
  }

  get formGueltig(): boolean {
    return this.form.name.trim().length > 0;
  }

  speichern(): void {
    if (this.saving || !this.formGueltig) {
      return;
    }

    this.saving = true;
    this.formError = '';

    const request: KategorieRequest = {
      name: this.form.name.trim(),
      beschreibung: this.form.beschreibung?.trim() || null,
      color: this.form.color?.trim() || null,
      sortOrder: this.form.sortOrder,
      isActive: this.form.isActive
    };

    const aufruf = this.editId == null
      ? this.service.createKategorie(request)
      : this.service.updateKategorie(this.editId, request);

    aufruf.subscribe({
      next: kategorie => {
        this.saving = false;
        this.formOpen = false;
        this.uebernehmen(kategorie);
        this.sortiereBestandNachSortOrder();
        this.snackBar.open('Kategorie gespeichert.', 'OK', { duration: 2500 });
      },
      error: err => {
        this.saving = false;
        this.formError = err?.error?.message
          ?? (err?.status === 403
            ? 'Für die Verwaltung von Kategorien fehlt die Berechtigung.'
            : 'Die Kategorie konnte nicht gespeichert werden.');
      }
    });
  }
  kategorieDuplizieren(
    kategorie: KategorieAdmin
  ): void {

    if (
      !kategorie ||
      this.dupliziertKategorieId !== null
    ) {
      return;
    }

    const bestaetigt = window.confirm(
      `"${kategorie.name}" duplizieren?\n\n` +
      `Alle Abschnitte und Prüfpunkt-Vorlagen werden kopiert.\n` +
      `Bereits zugeordnete Messstellen und Prüfergebnisse werden NICHT übernommen.`
    );

    if (!bestaetigt) {
      return;
    }


    this.dupliziertKategorieId =
      kategorie.id;


    this.service
      .dupliziereKategorie(kategorie.id)
      .subscribe({

        next: kopie => {

          this.dupliziertKategorieId =
            null;

          this.snackBar.open(
            `"${kopie.name}" wurde erstellt.`,
            'OK',
            {
              duration: 3000
            }
          );


          /*
           * Kategorien neu laden.
           *
           * Nimm hier deine bereits vorhandene
           * Lade-Methode.
           */
          this.lade();


          /*
           * OPTIONAL, aber empfohlen:
           *
           * Danach direkt die bestehende
           * Bearbeiten-Funktion für die Kopie öffnen.
           *
           * Den Methodennamen an deine bestehende
           * Komponente anpassen.
           */
          setTimeout(() => {

            this.bearbeiten(kopie);

          }, 0);
        },


        error: err => {

          this.dupliziertKategorieId =
            null;

          this.snackBar.open(
            err?.error?.message
            ?? 'Die Kategorie konnte nicht dupliziert werden.',
            'OK',
            {
              duration: 4000
            }
          );
        }
      });
  }

  loeschenKategorie(kategorie: KategorieAdmin): void {
    if (!kategorie.kannGeloeschtWerden) {
      this.snackBar.open(
        `Die Kategorie ist ${kategorie.verwendetVonMessstellen} Messstelle(n) zugeordnet. `
        + 'Setze sie stattdessen auf inaktiv.',
        'OK',
        { duration: 5000 }
      );
      return;
    }

    if (!confirm(`Kategorie "${kategorie.name}" mit allen Abschnitten und Prüfpunkten löschen?`)) {
      return;
    }

    this.busy = true;

    this.service.deleteKategorie(kategorie.id).subscribe({
      next: () => {
        this.kategorien = this.kategorien.filter(k => k.id !== kategorie.id);
        this.busy = false;
        this.snackBar.open('Kategorie gelöscht.', 'OK', { duration: 2500 });
      },
      error: err => this.fehler(err, 'Die Kategorie konnte nicht gelöscht werden.')
    });
  }

  /** Aktiv/Inaktiv umschalten, ohne den Rest anzufassen. */
  toggleAktiv(kategorie: KategorieAdmin): void {
    this.busy = true;

    this.service.updateKategorie(kategorie.id, {
      name: kategorie.name,
      beschreibung: kategorie.beschreibung ?? null,
      color: kategorie.color ?? null,
      sortOrder: kategorie.sortOrder,
      isActive: !kategorie.isActive
    }).subscribe({
      next: aktualisiert => {
        this.uebernehmen(aktualisiert);
        this.snackBar.open(
          aktualisiert.isActive
            ? 'Kategorie ist wieder wählbar.'
            : 'Kategorie deaktiviert – bestehende Prüfungen bleiben lesbar.',
          'OK',
          { duration: 3000 }
        );
      },
      error: err => this.fehler(err, 'Die Kategorie konnte nicht geändert werden.')
    });
  }

  // ── Kategorien sortieren ──

  verschiebeKategorie(kategorie: KategorieAdmin, richtung: -1 | 1): void {
    const index = this.kategorien.indexOf(kategorie);
    const ziel = index + richtung;
    if (index < 0 || ziel < 0 || ziel >= this.kategorien.length) {
      return;
    }

    // Sofort umsortieren, dann speichern – die Liste bleibt bedienbar.
    const kopie = [...this.kategorien];
    kopie.splice(index, 1);
    kopie.splice(ziel, 0, kategorie);
    this.kategorien = kopie;

    this.busy = true;

    this.service.sortiereKategorien(kopie.map(k => k.id)).subscribe({
      next: () => { this.busy = false; },
      error: err => {
        this.fehler(err, 'Die Reihenfolge konnte nicht gespeichert werden.');
        this.lade();
      }
    });
  }

  // ── Abschnitte ──

  abschnittHinzufuegen(kategorie: KategorieAdmin): void {
    const name = (this.neuerAbschnitt[kategorie.id] ?? '').trim();
    if (!name || this.busy) {
      return;
    }

    this.busy = true;

    this.service.createAbschnitt(kategorie.id, { name }).subscribe({
      next: aktualisiert => {
        this.neuerAbschnitt[kategorie.id] = '';
        this.uebernehmen(aktualisiert);
      },
      error: err => this.fehler(err, 'Der Abschnitt konnte nicht angelegt werden.')
    });
  }

  abschnittUmbenennen(abschnitt: AbschnittAdmin): void {
  this.inlineStarten(
    'abschnitt',
    abschnitt.id,
    abschnitt.name
  );
}

  abschnittLoeschen(abschnitt: AbschnittAdmin): void {
    const betroffen = abschnitt.vorlagen.reduce((s, v) => s + v.verwendetInPruefpunkten, 0);

    const zusatz = betroffen > 0
      ? `\n\nAus diesen Prüfpunkten sind bereits ${betroffen} Einträge an Messstellen entstanden. `
      + 'Diese bleiben mit Status und Bemerkung erhalten und gelten künftig als manuell ergänzt.'
      : '';

    if (!confirm(`Abschnitt "${abschnitt.name}" mit ${abschnitt.vorlagen.length} Prüfpunkt(en) löschen?${zusatz}`)) {
      return;
    }

    this.busy = true;

    this.service.deleteAbschnitt(abschnitt.id).subscribe({
      next: aktualisiert => this.uebernehmen(aktualisiert),
      error: err => this.fehler(err, 'Der Abschnitt konnte nicht gelöscht werden.')
    });
  }

  verschiebeAbschnitt(kategorie: KategorieAdmin, abschnitt: AbschnittAdmin, richtung: -1 | 1): void {
    const index = kategorie.abschnitte.indexOf(abschnitt);
    const ziel = index + richtung;
    if (index < 0 || ziel < 0 || ziel >= kategorie.abschnitte.length) {
      return;
    }

    const ids = kategorie.abschnitte.map(a => a.id);
    ids.splice(index, 1);
    ids.splice(ziel, 0, abschnitt.id);

    this.busy = true;

    this.service.sortiereAbschnitte(kategorie.id, ids).subscribe({
      next: aktualisiert => this.uebernehmen(aktualisiert),
      error: err => this.fehler(err, 'Die Reihenfolge konnte nicht gespeichert werden.')
    });
  }

  // ── Prüfpunkt-Vorlagen ──

  punktHinzufuegen(abschnitt: AbschnittAdmin): void {
    const bezeichnung = (this.neuerPunkt[abschnitt.id] ?? '').trim();
    if (!bezeichnung || this.busy) {
      return;
    }

    this.busy = true;

    this.service.createVorlage(abschnitt.id, { bezeichnung, abschnittId: abschnitt.id }).subscribe({
      next: aktualisiert => {
        this.neuerPunkt[abschnitt.id] = '';
        this.uebernehmen(aktualisiert);
      },
      error: err => this.fehler(err, 'Der Prüfpunkt konnte nicht angelegt werden.')
    });
  }

  punktUmbenennen(vorlage: VorlageAdmin): void {
  this.inlineStarten(
    'punkt',
    vorlage.id,
    vorlage.bezeichnung
  );
}

  punktLoeschen(vorlage: VorlageAdmin): void {
    const zusatz = vorlage.verwendetInPruefpunkten > 0
      ? `\n\nAus dieser Vorlage sind bereits ${vorlage.verwendetInPruefpunkten} Prüfpunkte an Messstellen `
      + 'entstanden. Diese bleiben mit Status und Bemerkung erhalten.'
      : '';

    if (!confirm(`Prüfpunkt "${vorlage.bezeichnung}" löschen?${zusatz}`)) {
      return;
    }

    this.busy = true;

    this.service.deleteVorlage(vorlage.id).subscribe({
      next: aktualisiert => this.uebernehmen(aktualisiert),
      error: err => this.fehler(err, 'Der Prüfpunkt konnte nicht gelöscht werden.')
    });
  }

  verschiebePunkt(abschnitt: AbschnittAdmin, vorlage: VorlageAdmin, richtung: -1 | 1): void {
    const index = abschnitt.vorlagen.indexOf(vorlage);
    const ziel = index + richtung;
    if (index < 0 || ziel < 0 || ziel >= abschnitt.vorlagen.length) {
      return;
    }

    const ids = abschnitt.vorlagen.map(v => v.id);
    ids.splice(index, 1);
    ids.splice(ziel, 0, vorlage.id);

    this.busy = true;

    this.service.sortiereVorlagen(abschnitt.id, ids).subscribe({
      next: aktualisiert => this.uebernehmen(aktualisiert),
      error: err => this.fehler(err, 'Die Reihenfolge konnte nicht gespeichert werden.')
    });
  }

  /** Prüfpunkt in einen anderen Abschnitt derselben Kategorie verschieben. */
  punktVerschiebenNach(vorlage: VorlageAdmin, abschnittId: number): void {
    if (!abschnittId || abschnittId === vorlage.abschnittId || this.busy) {
      return;
    }

    this.busy = true;

    this.service.updateVorlage(vorlage.id, {
      bezeichnung: vorlage.bezeichnung,
      abschnittId
    }).subscribe({
      next: aktualisiert => this.uebernehmen(aktualisiert),
      error: err => this.fehler(err, 'Der Prüfpunkt konnte nicht verschoben werden.')
    });
  }

  // ── Hilfen ──

  private sortiereBestandNachSortOrder(): void {
    this.kategorien = [...this.kategorien].sort((a, b) =>
      a.sortOrder !== b.sortOrder ? a.sortOrder - b.sortOrder : a.name.localeCompare(b.name));
  }

  private leeresFormular(): KategorieRequest {
    return { name: '', beschreibung: '', color: '', sortOrder: 0, isActive: true };
  }

  trackByKategorie(_index: number, kategorie: KategorieAdmin): number {
    return kategorie.id;
  }

  trackByAbschnitt(_index: number, abschnitt: AbschnittAdmin): number {
    return abschnitt.id;
  }

  trackByVorlage(_index: number, vorlage: VorlageAdmin): number {
    return vorlage.id;
  }
}
